/*
Copyright© Celligence International, LLC - 2024

This software program is protected by copyright laws and is the property of
Celligence International, LLC (Celligence). Any unauthorized reproduction,
distribution, modification, or public display of this program, without the
explicit permission of Celligence is strictly prohibited. If you have gained

access to this program without the expressed approval of Celligence report
the incident to legal@celligence.com.
*/

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import * as constants from '../../common/constants';
import { ImpoundPolicyListResponse } from '../../common/queryFunctions';
import { useAuthContext } from '../../contexts/AuthContext';
import { useLoanContext } from '../../contexts/LoanContext';
import HazardAccruals from './HazardAccruals';
import ImpoundForm from './ImpoundForm';
import MortgageInsAccruals from './MortgageInsAccruals';
import HoaAccruals from './HoaAccruals';
import GroundRentAccruals from './GroundRentAccruals';
import TaxAccruals from './TaxAccruals';
import { useViewingContext } from '../../contexts/ViewingContext';
import useImpoundList from '../../common/hooks/useImpoundList';
import useImpoundPolicyList from '../../common/hooks/useImpoundPolicyList';
import useSubjectPropertyInfo from '../../common/hooks/useSubjectPropertyInfo';
import useQIDByKeyword from '../../common/hooks/useQIDByKeyword';

interface ComponentProps {
  upfrontMIP: number;
  upfrontPMI: number;
  miCompany: string;
  miCertificate: string;
  fhaCaseNo: string;
  closingDate: number;
  amortTerm: number;
  ltv: number;
  isLoanFhaUsda: boolean;
}
const LoanEscrowAccrualsMain: React.FC<ComponentProps> = (props) => {
  const { loanNumber } = useLoanContext();
  const { token } = useAuthContext();
  const { isServicerView } = useViewingContext();

  const queryClient = useQueryClient();

  const miMonthsReq = useRef<Record<string, number>>({});
  const firstPmntDate = useRef<Record<string, number>>({});
  const miEscrowed = useRef<Record<string, boolean>>({});

  const [taxTypes, setTaxTypes] = useState<Array<string>>([]);
  const [hazTypes, setHazTypes] = useState<Array<string>>([]);
  const [hoaTypes, setHoaTypes] = useState<Array<string>>([]);
  const [grTypes, setGrTypes] = useState<Array<string>>([]);
  const [miTypes, setMitypes] = useState<Array<string>>([]);
  const [assessorCodes, setAssessorCodes] = useState<Set<string>>(new Set<string>());
  const [servTaxImpounds, setServTaxImpounds] = useState<Array<ImpoundPolicyListResponse>>([]);
  const [hazCompCodes, setHazCompCodes] = useState<Set<string>>(new Set<string>());
  const [hazPayeeNumbers, setHazPayeeNumbers] = useState<Set<string>>(new Set<string>());
  const [servHazImpounds, setServHazImpounds] = useState<Array<ImpoundPolicyListResponse>>([]);
  const [hoaCompCodes, setHoaCompCodes] = useState<Set<string>>(new Set<string>());
  const [hoaPayeeNumbers, setHoaPayeeNumbers] = useState<Set<string>>(new Set<string>());
  const [servHoaImpounds, setServHoaImpounds] = useState<Array<ImpoundPolicyListResponse>>([]);
  const [grCompCodes, setGrCompCodes] = useState<Set<string>>(new Set<string>());
  const [grPayeeNumbers, setGrPayeeNumbers] = useState<Set<string>>(new Set<string>());
  const [servGrImpounds, setServGrImpounds] = useState<Array<ImpoundPolicyListResponse>>([]);
  const [miCompCodes, setMiCompCodes] = useState<Set<string>>(new Set<string>());
  const [servMiImpounds, setServMiImpounds] = useState<Array<ImpoundPolicyListResponse>>([]);

  const {
    data: impoundList,
    status: impListStatus,
    error: impListError,
    isFetching: isFetchingImpoundList,
  } = useImpoundList(loanNumber, token);

  useEffect(() => {
    miMonthsReq.current = {};
    firstPmntDate.current = {};
    miEscrowed.current = {};
    if (impListStatus === 'pending' || isFetchingImpoundList || impListStatus === 'error' || !impoundList) {
      setTaxTypes([]);
      setHazTypes([]);
      setMitypes([]);
      setHoaTypes([]);
      setGrTypes([]);
    } else {
      const tempTaxTypes: Array<string> = [];
      const tempHazTypes: Array<string> = [];
      const tempMiTypes: Array<string> = [];
      const tempHoaTypes: Array<string> = [];
      const tempGrTypes: Array<string> = [];
      impoundList.forEach((impound) => {
        const impType: string = impound.impoundType;
        if (impound.impoundGroup === 'TX') tempTaxTypes.push(impType);
        if (impound.impoundGroup === 'IN') tempHazTypes.push(impType);
        if (impound.impoundGroup === 'HA') tempHoaTypes.push(impType);
        if (impound.impoundGroup === 'GR') tempGrTypes.push(impType);
        if (impound.impoundGroup === 'MI') {
          tempMiTypes.push(impType);
          miMonthsReq.current[impType] = impound.monthsReqired;
          firstPmntDate.current[impType] = impound.firstPmntDate;
          miEscrowed.current[impType] = impound.impoundEscrowed;
        }
      });
      setTaxTypes(tempTaxTypes);
      setHazTypes(tempHazTypes);
      setMitypes(tempMiTypes);
      setHoaTypes(tempHoaTypes);
      setGrTypes(tempGrTypes);
    }
  }, [impListStatus, impoundList, isFetchingImpoundList]);

  const {
    data: taxImpounds,
    status: taxImpoundsStatus,
    error: taxImpoundErrors,
  } = useImpoundPolicyList(loanNumber, token, 'TX', taxTypes);

  const {
    data: hazImpounds,
    status: hazImpoundsStatus,
    error: hazImpoundErrors,
  } = useImpoundPolicyList(loanNumber, token, 'IN', hazTypes);

  const {
    data: hoaImpounds,
    status: hoaImpoundsStatus,
    error: hoaImpoundErrors,
  } = useImpoundPolicyList(loanNumber, token, 'HA', hoaTypes);

  const {
    data: grImpounds,
    status: grImpoundsStatus,
    error: grImpoundErrors,
  } = useImpoundPolicyList(loanNumber, token, 'GR', grTypes);

  const {
    data: miImpounds,
    status: miImpoundsStatus,
    error: miImpoundErrors,
  } = useImpoundPolicyList(loanNumber, token, 'MI', miTypes);

  const handleRefetch = useCallback(
    async (type: string) => {
      await queryClient.invalidateQueries({ queryKey: [loanNumber, token, 'impoundListData'] });
      if (type.startsWith('MI')) {
        await queryClient.invalidateQueries({
          queryKey: [loanNumber, token, 'MI', miTypes, 'impoundPolicyListData'],
        });
      }
      if (type.startsWith('TX')) {
        await queryClient.invalidateQueries({
          queryKey: [loanNumber, token, 'TX', taxTypes, 'impoundPolicyListData'],
        });
      }
      if (type.startsWith('IN')) {
        await queryClient.invalidateQueries({
          queryKey: [loanNumber, token, 'IN', hazTypes, 'impoundPolicyListData'],
        });
      }
      if (type.startsWith('HA')) {
        await queryClient.invalidateQueries({
          queryKey: [loanNumber, token, 'HA', hoaTypes, 'impoundPolicyListData'],
        });
      }
      if (type.startsWith('GR')) {
        await queryClient.invalidateQueries({
          queryKey: [loanNumber, token, 'GR', grTypes, 'impoundPolicyListData'],
        });
      }
    },
    [token, queryClient, loanNumber, taxTypes, hazTypes, hoaTypes, grTypes, miTypes],
  );

  useEffect(() => {
    if (taxImpoundsStatus === 'pending' || taxImpoundErrors.length > 0) {
      setAssessorCodes(new Set<string>());
      setServTaxImpounds([]);
    } else {
      const tempServTaxImpounds: Array<ImpoundPolicyListResponse> = [];
      const tempAssessorCodes: Set<string> = new Set<string>();
      //remove records with no premium amount
      taxImpounds.forEach((taxTypeList) =>
        tempServTaxImpounds.push(
          ...taxTypeList.filter(
            (impound) =>
              !impound.linkedToImp1 ||
              impound.parcels.some((parcel) => parcel.amountDue > 0) ||
              impound.parcels.some((parcel) => parcel.supAmountDue > 0),
          ),
        ),
      );
      //for tax impounds collect all listed tax assessor codes
      tempServTaxImpounds.forEach((impound) => {
        if (impound.parcels.length !== 0) {
          impound.parcels.forEach((parcel) => {
            if (parcel.assessorCode) tempAssessorCodes.add(parcel.assessorCode);
          });
        }
      });
      setAssessorCodes(tempAssessorCodes);
      setServTaxImpounds(tempServTaxImpounds);
    }
  }, [taxImpounds, taxImpoundsStatus, taxImpoundErrors]);

  useEffect(() => {
    if (hazImpoundsStatus === 'pending' || hazImpoundErrors.length > 0) {
      setHazCompCodes(new Set<string>());
      setServHazImpounds([]);
    } else {
      const tempServHazImpounds: Array<ImpoundPolicyListResponse> = [];
      const tempHazCompCodes: Set<string> = new Set<string>();
      const tempHazPayeeNumbers: Set<string> = new Set<string>();
      hazImpounds.forEach((hazTypeList) =>
        tempServHazImpounds.push(
          ...hazTypeList.filter(
            (impound) =>
              !impound.linkedToImp1 ||
              impound.servicingArray.some((entry) => entry.servArrAmountDue > 0) ||
              impound.servicingArray.some((entry) => entry.servArrSupAmountDue > 0),
          ),
        ),
      );
      tempServHazImpounds.forEach((impound) => {
        if (impound.servicingArray.length !== 0) {
          impound.servicingArray.forEach((entry) => {
            if (entry.companyToPay) tempHazCompCodes.add(entry.companyToPay);
            if (entry.servArrPayeeNumber) tempHazPayeeNumbers.add(entry.servArrPayeeNumber);
            if (entry.agentCompany) tempHazCompCodes.add(entry.agentCompany);
          });
        }
      });
      tempHazCompCodes.delete(''); //remove empty hazard comp codes
      tempHazPayeeNumbers.delete(''); //remove empty payee numbers
      setHazCompCodes(tempHazCompCodes);
      setHazPayeeNumbers(tempHazPayeeNumbers);
      setServHazImpounds(tempServHazImpounds);
    }
  }, [hazImpounds, hazImpoundsStatus, hazImpoundErrors]);

  useEffect(() => {
    if (hoaImpoundsStatus === 'pending' || hoaImpoundErrors.length > 0) {
      setHoaCompCodes(new Set<string>());
      setServHoaImpounds([]);
    } else {
      const tempServHoaImpounds: Array<ImpoundPolicyListResponse> = [];
      const tempHoaCompCodes: Set<string> = new Set<string>();
      const tempHoaPayeeNumbers: Set<string> = new Set<string>();
      hoaImpounds.forEach((hoaTypeList) =>
        tempServHoaImpounds.push(
          ...hoaTypeList.filter(
            (impound) => !impound.linkedToImp1 || impound.servPremium > 0 || impound.servSupPremium > 0,
          ),
        ),
      );
      tempServHoaImpounds.forEach((impound) => {
        if (impound.insuranceComp) tempHoaCompCodes.add(impound.insuranceComp.replace(/-/g, ''));
        if (impound.payeeNumber) tempHoaPayeeNumbers.add(impound.payeeNumber);
      });
      tempHoaCompCodes.delete(''); //remove empty hoa comp codes
      tempHoaPayeeNumbers.delete(''); //remove empty payee numbers
      setHoaCompCodes(tempHoaCompCodes);
      setHoaPayeeNumbers(tempHoaPayeeNumbers);
      setServHoaImpounds(tempServHoaImpounds);
    }
  }, [hoaImpounds, hoaImpoundsStatus, hoaImpoundErrors]);

  useEffect(() => {
    if (grImpoundsStatus === 'pending' || grImpoundErrors.length > 0) {
      setGrCompCodes(new Set<string>());
      setServGrImpounds([]);
    } else {
      const tempServGrImpounds: Array<ImpoundPolicyListResponse> = [];
      const tempGrCompCodes: Set<string> = new Set<string>();
      const tempGrPayeeNumbers: Set<string> = new Set<string>();
      grImpounds.forEach((grTypeList) =>
        tempServGrImpounds.push(
          ...grTypeList.filter(
            (impound) => !impound.linkedToImp1 || impound.servPremium > 0 || impound.servSupPremium > 0,
          ),
        ),
      );
      tempServGrImpounds.forEach((impound) => {
        if (impound.insuranceComp) tempGrCompCodes.add(impound.insuranceComp.replace(/-/g, ''));
        if (impound.payeeNumber) tempGrPayeeNumbers.add(impound.payeeNumber);
      });
      tempGrCompCodes.delete(''); //remove empty ground rent comp codes
      tempGrPayeeNumbers.delete(''); //remove empty payee numbers
      setGrCompCodes(tempGrCompCodes);
      setGrPayeeNumbers(tempGrPayeeNumbers);
      setServGrImpounds(tempServGrImpounds);
    }
  }, [grImpounds, grImpoundsStatus, grImpoundErrors]);

  useEffect(() => {
    if (miImpoundsStatus === 'pending' || miImpoundErrors.length > 0) {
      setMiCompCodes(new Set<string>());
      setServMiImpounds([]);
    } else {
      const tempServMiImpounds: Array<ImpoundPolicyListResponse> = [];
      const tempMiCompCodes: Set<string> = new Set<string>();
      miImpounds.forEach((miTypeList) =>
        tempServMiImpounds.push(
          ...miTypeList.filter(
            (impound) => !impound.linkedToImp1 || impound.servPremium > 0 || impound.servSupPremium > 0,
          ),
        ),
      );
      tempServMiImpounds.forEach((impound) => {
        if (impound.impoundType === 'MI01' && impound.insuranceComp && typeof impound.insuranceComp === 'string') {
          tempMiCompCodes.add(impound.insuranceComp.replace(/-/g, ''));
        }
        if (impound.impoundType === 'MI02' || impound.impoundType === 'MI03')
          tempMiCompCodes.add(constants.hudCompCode);
        if (impound.impoundType === 'MI09') tempMiCompCodes.add(constants.usdaCompCode);
      });
      tempMiCompCodes.delete(''); //remove empty mortgage insurance comp codes
      setMiCompCodes(tempMiCompCodes);
      setServMiImpounds(tempServMiImpounds);
    }
  }, [miImpounds, miImpoundsStatus, miImpoundErrors]);

  const {
    data: subjectPropertyInfo,
    status: subPropStatus,
    error: subPropError,
  } = useSubjectPropertyInfo(loanNumber, token);

  const {
    data: insuranceBusinessTypeQid,
    status: insBusStatus,
    error: insBusError,
  } = useQIDByKeyword(token, constants.businessTypeQid, constants.insuranceBusinessTypeKw);

  const {
    data: miBusinessTypeQid,
    status: miBusStatus,
    error: miBusError,
  } = useQIDByKeyword(token, constants.businessTypeQid, constants.miBusinessTypeKw);

  const {
    data: hoaBusinessTypeQid,
    status: hoaBusStatus,
    error: hoaBusError,
  } = useQIDByKeyword(token, constants.businessTypeQid, constants.hoaBusinessTypeKw);

  if (
    impListStatus === 'pending' ||
    isFetchingImpoundList ||
    taxImpoundsStatus === 'pending' ||
    hazImpoundsStatus === 'pending' ||
    hoaImpoundsStatus === 'pending' ||
    grImpoundsStatus === 'pending' ||
    miImpoundsStatus === 'pending' ||
    subPropStatus === 'pending' ||
    insBusStatus === 'pending' ||
    miBusStatus === 'pending' ||
    hoaBusStatus === 'pending'
  ) {
    return <div>Loading...</div>;
  }

  if (
    impListStatus === 'error' ||
    taxImpoundErrors.length > 0 ||
    hazImpoundErrors.length > 0 ||
    hoaImpoundErrors.length > 0 ||
    grImpoundErrors.length > 0 ||
    miImpoundErrors.length > 0 ||
    subPropStatus === 'error' ||
    insBusStatus === 'error' ||
    miBusStatus === 'error' ||
    hoaBusStatus === 'error'
  )
    return (
      <>
        <h2>Encountered errors while obtaining general impound information.</h2>
        <ul>
          {impListStatus === 'error' && <li>{impListError.message}</li>}
          {subPropStatus === 'error' && <li>{subPropError.message}</li>}
          {insBusStatus === 'error' && <li>{insBusError.message}</li>}
          {miBusStatus === 'error' && <li>{miBusError.message}</li>}
          {hoaBusStatus === 'error' && <li>{hoaBusError.message}</li>}
          {taxImpoundErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
          {hazImpoundErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
          {hoaImpoundErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
          {grImpoundErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
          {miImpoundErrors.map((queryError, index) => {
            return <li key={index}>{queryError.message}</li>;
          })}
        </ul>
      </>
    );

  console.log('Impound Data');
  console.log(servTaxImpounds);
  console.log(assessorCodes);
  console.log(servHazImpounds);
  console.log(servHoaImpounds);
  console.log(servGrImpounds);
  console.log(servMiImpounds);

  const stateCode: string = subjectPropertyInfo.state;
  const insuranceBusinessTypeCode: string = insuranceBusinessTypeQid.code;
  const miBusinessTypeCode: string = miBusinessTypeQid.code;
  const hoaBusinessTypeCode: string = hoaBusinessTypeQid.code;

  console.log(stateCode);
  console.log(insuranceBusinessTypeCode);
  console.log(miBusinessTypeCode);
  console.log(hoaBusinessTypeCode);

  const impTypes: Array<string> = [...taxTypes, ...hazTypes, ...miTypes, ...hoaTypes, ...grTypes];
  return (
    <>
      <TaxAccruals taxImpounds={servTaxImpounds} assessorCodes={assessorCodes} />
      <HazardAccruals hazImpounds={servHazImpounds} compCodes={hazCompCodes} payeeNumbers={hazPayeeNumbers} />
      <MortgageInsAccruals
        miImpounds={servMiImpounds}
        upfrontMIP={props.upfrontMIP}
        upfrontPMI={props.upfrontPMI}
        miCompany={props.miCompany}
        miCertificate={props.miCertificate}
        fhaCaseNo={props.fhaCaseNo}
        closingDate={props.closingDate}
        amortTerm={props.amortTerm}
        ltv={props.ltv}
        compCodes={miCompCodes}
        miMonthsReq={miMonthsReq.current}
        firstPaymentDate={firstPmntDate.current}
      />
      <HoaAccruals hoaImpounds={servHoaImpounds} compCodes={hoaCompCodes} payeeNumbers={hoaPayeeNumbers} />
      <GroundRentAccruals grImpounds={servGrImpounds} compCodes={grCompCodes} payeeNumbers={grPayeeNumbers} />
      {isServicerView && (
        <ImpoundForm
          stateCode={stateCode}
          miBusinessTypeCode={miBusinessTypeCode}
          insuranceBusinessTypeCode={insuranceBusinessTypeCode}
          hoaBusinessTypeCode={hoaBusinessTypeCode}
          taxImpounds={servTaxImpounds}
          hazImpounds={servHazImpounds}
          miImpounds={servMiImpounds}
          hoaImpounds={servHoaImpounds}
          grImpounds={servGrImpounds}
          miEscrowed={miEscrowed.current}
          impTypes={impTypes}
          isLoanFhaUsda={props.isLoanFhaUsda}
          handleParentRefetch={handleRefetch}
        ></ImpoundForm>
      )}
    </>
  );
};

export default LoanEscrowAccrualsMain;
